import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import {
  Mail,
  Search,
  Plus,
  RefreshCw,
  Trash2,
  Copy,
  Check,
  Clock,
  CheckCircle2,
  XCircle,
  Gift,
  Megaphone,
  Eye,
  MousePointerClick,
  BellRing,
  Send,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import TenantInviteDialog from "./TenantInviteDialog";
import BulkInviteDialog from "./BulkInviteDialog";

const STATUSES = ["all", "warm", "pending", "accepted", "expired"];
// Sıcak aday: e-postayı açmış ya da davet linkine tıklamış ama henüz
// üye olmamış — kişisel takip için en değerli kitle.
const isWarm = (i) => !i.accepted && Boolean(i.opened_at || i.visited_at);
const STATUS_STYLE = {
  pending: { bg: "bg-amber-100 text-amber-800 ring-amber-200", icon: Clock },
  accepted: {
    bg: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    icon: CheckCircle2,
  },
  expired: { bg: "bg-zinc-100 text-zinc-600 ring-zinc-200", icon: XCircle },
};

export default function PlatformTenantInvitations() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [followingUpId, setFollowingUpId] = useState(null);

  const followUp = async (inv) => {
    setFollowingUpId(inv.id);
    try {
      await api.post(`/platform/tenant-invitations/${inv.id}/follow-up`);
      toast.success(`Takip e-postası ${inv.email} adresine gönderildi`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Takip e-postası gönderilemedi");
    } finally {
      setFollowingUpId(null);
    }
  };

  const sendReport = async () => {
    setSendingReport(true);
    try {
      const { data } = await api.post("/platform/invite-report/send-now");
      toast.success(`Haftalık davet raporu ${data.sent} platform adminine gönderildi`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Rapor gönderilemedi");
    } finally {
      setSendingReport(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(
        `/platform/tenant-invitations?${params.toString()}`,
      );
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Durum sekmesi artık istemci tarafında filtreler — böylece huni
  // istatistikleri her zaman TÜM davetler üzerinden hesaplanır.
  const [campaignFilter, setCampaignFilter] = useState("all");
  const campaigns = useMemo(() => {
    const set = new Set();
    for (const i of items) if (i.campaign) set.add(i.campaign);
    return Array.from(set).sort((a, b) => a.localeCompare(b, "tr"));
  }, [items]);
  const filtered = useMemo(() => {
    let base = items;
    if (campaignFilter !== "all")
      base = base.filter((i) => (i.campaign || "") === campaignFilter);
    if (statusFilter === "all") return base;
    if (statusFilter === "warm") return base.filter(isWarm);
    return base.filter((i) => i.status === statusFilter);
  }, [items, statusFilter, campaignFilter]);

  // Kampanya karşılaştırması — her kampanyanın dönüşüm hunisi.
  const campaignStats = useMemo(() => {
    const map = new Map();
    for (const i of items) {
      const key = i.campaign || "—";
      if (!map.has(key))
        map.set(key, { campaign: key, sent: 0, opened: 0, clicked: 0, accepted: 0 });
      const s = map.get(key);
      s.sent += 1;
      if (i.opened_at || i.visited_at || i.accepted) s.opened += 1;
      if (i.visited_at || i.accepted) s.clicked += 1;
      if (i.accepted) s.accepted += 1;
    }
    return Array.from(map.values()).sort((a, b) => b.sent - a.sent);
  }, [items]);

  // Dönüşüm hunisi: gönderilen → açılan → linke tıklayan → kabul eden.
  // Alt aşamalar üst aşamayı da ima eder (tıklayan = açmıştır vb.).
  const funnel = useMemo(() => {
    const sent = items.length;
    const opened = items.filter((i) => i.opened_at || i.visited_at || i.accepted).length;
    const clicked = items.filter((i) => i.visited_at || i.accepted).length;
    const accepted = items.filter((i) => i.accepted).length;
    const pct = (n) => (sent ? Math.round((n / sent) * 100) : 0);
    return { sent, opened, clicked, accepted, pct };
  }, [items]);

  const counts = useMemo(() => {
    const c = { all: items.length, warm: 0, pending: 0, accepted: 0, expired: 0 };
    for (const i of items) {
      c[i.status] = (c[i.status] || 0) + 1;
      if (isWarm(i)) c.warm += 1;
    }
    return c;
  }, [items]);

  const resend = async (id) => {
    try {
      await api.post(`/platform/tenant-invitations/${id}/resend`);
      toast.success(t("platform.invitations.resent"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/platform/tenant-invitations/${id}`);
      toast.success(t("platform.invitations.revoked"));
      setConfirmRevoke(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyToken = async (id, token) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1200);
    } catch {
      /* no-op */
    }
  };

  return (
    <div className="space-y-5" data-testid="platform-invitations">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-600">
            Serpix Admin
          </div>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            <Gift className="h-7 w-7 text-violet-600" />
            {t("platform.nav.invitations")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t("platform.invitations.subtitle")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={sendReport}
            disabled={sendingReport}
            data-testid="platform-invite-report-btn"
            className="gap-1.5"
            title="Dönüşüm raporunu şimdi e-posta ile gönder (her Pazartesi 09:00'da otomatik gönderilir)"
          >
            {sendingReport ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            Haftalık Rapor
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowBulkInvite(true)}
            data-testid="platform-bulk-invite-btn"
            className="gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50"
          >
            <Megaphone className="h-4 w-4" /> Toplu Davet
          </Button>
          <Button
            onClick={() => setShowInvite(true)}
            data-testid="platform-invitation-new-btn"
            className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            {t("platform.invitations.new")}
          </Button>
        </div>
      </header>

      {/* Dönüşüm hunisi — kampanya davetlerinin ne kadarı açıldı/tıklandı/kabul edildi */}
      {items.length > 0 && (
        <div
          className="grid grid-cols-2 gap-2 sm:grid-cols-4"
          data-testid="invitations-funnel"
        >
          {[
            { key: "sent", label: "Gönderilen", value: funnel.sent, icon: Mail, tone: "text-zinc-700 bg-white", showPct: false },
            { key: "opened", label: "Açılan", value: funnel.opened, icon: Eye, tone: "text-sky-700 bg-sky-50", showPct: true },
            { key: "clicked", label: "Linke Tıklayan", value: funnel.clicked, icon: MousePointerClick, tone: "text-amber-700 bg-amber-50", showPct: true },
            { key: "accepted", label: "Kabul Eden", value: funnel.accepted, icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50", showPct: true },
          ].map((s) => {
            const SIcon = s.icon;
            return (
              <div
                key={s.key}
                data-testid={`invitations-funnel-${s.key}`}
                className={`rounded-xl border border-zinc-200 p-3 ${s.tone.split(" ")[1]}`}
              >
                <div className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-500">
                  <SIcon className={`h-3.5 w-3.5 ${s.tone.split(" ")[0]}`} />
                  {s.label}
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className={`text-2xl font-semibold ${s.tone.split(" ")[0]}`}>
                    {s.value}
                  </span>
                  {s.showPct && (
                    <span className="text-[11px] font-medium text-zinc-400">
                      %{funnel.pct(s.value)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Kampanya karşılaştırma — hangi mesaj daha çok üye getirdi? */}
      {campaigns.length > 1 && (
        <div
          className="overflow-x-auto rounded-xl border border-zinc-200 bg-white"
          data-testid="campaign-compare"
        >
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-zinc-100 text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-2">Kampanya</th>
                <th className="px-3 py-2">Gönderilen</th>
                <th className="px-3 py-2">Açılan</th>
                <th className="px-3 py-2">Tıklayan</th>
                <th className="px-3 py-2">Kabul</th>
                <th className="px-3 py-2">Dönüşüm</th>
              </tr>
            </thead>
            <tbody>
              {campaignStats.map((s) => {
                const conv = s.sent ? Math.round((s.accepted / s.sent) * 100) : 0;
                return (
                  <tr
                    key={s.campaign}
                    className="border-b border-zinc-50 last:border-0"
                    data-testid={`campaign-row-${s.campaign}`}
                  >
                    <td className="px-3 py-2 font-medium text-zinc-800">
                      {s.campaign}
                    </td>
                    <td className="px-3 py-2 text-zinc-600">{s.sent}</td>
                    <td className="px-3 py-2 text-sky-700">{s.opened}</td>
                    <td className="px-3 py-2 text-amber-700">{s.clicked}</td>
                    <td className="px-3 py-2 text-emerald-700">{s.accepted}</td>
                    <td className="px-3 py-2">
                      <span
                        className={[
                          "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                          conv >= 20
                            ? "bg-emerald-100 text-emerald-800"
                            : conv > 0
                              ? "bg-amber-100 text-amber-800"
                              : "bg-zinc-100 text-zinc-500",
                        ].join(" ")}
                      >
                        %{conv}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Status tabs */}
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="platform-invitations-tabs"
      >
        {STATUSES.map((s) => {
          const active = statusFilter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              data-testid={`platform-invitations-tab-${s}`}
              className={[
                "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                active
                  ? "border-violet-600 bg-violet-600 text-white shadow-md shadow-violet-500/20"
                  : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50",
              ].join(" ")}
            >
              <span>
                {s === "all"
                  ? t("platform.tenants.all")
                  : s === "warm"
                    ? "🔥 İlgilenenler"
                    : t(`platform.invitations.status_${s}`)}
              </span>
              <span
                className={[
                  "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold",
                  active
                    ? "bg-white/95 text-violet-700"
                    : s === "warm" && counts.warm > 0
                      ? "bg-amber-100 text-amber-800"
                      : "bg-zinc-100 text-zinc-700",
                ].join(" ")}
              >
                {counts[s] || 0}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="platform-invitations-search"
            placeholder={t("platform.invitations.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {loading && (
          <div className="rounded-xl border border-zinc-200 bg-white py-10 text-center text-sm text-zinc-500">
            {t("common.loading")}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div
            className="rounded-xl border border-dashed border-zinc-300 bg-white/60 py-14 text-center"
            data-testid="platform-invitations-empty"
          >
            <Gift className="mx-auto h-8 w-8 text-zinc-300" />
            <p className="mt-3 text-sm font-medium text-zinc-600">
              {t("platform.invitations.empty_title")}
            </p>
            <p className="mt-1 text-xs text-zinc-500">
              {t("platform.invitations.empty_hint")}
            </p>
            <Button
              onClick={() => setShowInvite(true)}
              className="mt-4 bg-gradient-to-r from-violet-600 to-indigo-600 text-white"
              data-testid="platform-invitations-empty-cta"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              {t("platform.invitations.new")}
            </Button>
          </div>
        )}
        {filtered.map((inv) => {
          const st = STATUS_STYLE[inv.status] || STATUS_STYLE.pending;
          const StIcon = st.icon;
          const warm = isWarm(inv);
          return (
            <div
              key={inv.id}
              data-testid={`platform-invitation-${inv.id}`}
              className={[
                "rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md",
                warm
                  ? "border-amber-300 bg-gradient-to-r from-amber-50/70 to-white ring-1 ring-amber-200"
                  : "border-zinc-200",
              ].join(" ")}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${st.bg}`}
                    >
                      <StIcon className="h-3 w-3" />
                      {t(`platform.invitations.status_${inv.status}`)}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800">
                      {t("platform.invitations.months", {
                        count: inv.trial_months || 1,
                      })}
                    </span>
                    {inv.campaign && inv.campaign !== "bulk" && (
                      <span
                        data-testid={`invitation-campaign-${inv.id}`}
                        className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-rose-200"
                      >
                        {inv.campaign}
                      </span>
                    )}
                    {(inv.modules || []).length > 0 &&
                      inv.modules.map((m) => (
                        <span
                          key={m}
                          data-testid={`invitation-module-${inv.id}-${m}`}
                          className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 ring-1 ring-indigo-200"
                        >
                          {m === "cop" ? "COP31" : m.toUpperCase()}
                        </span>
                      ))}
                    <span className="text-[10px] text-zinc-500">
                      {new Date(inv.created_at).toLocaleDateString()}
                    </span>
                    {(inv.opened_at || inv.visited_at || inv.accepted) && (
                      <span
                        data-testid={`invitation-opened-${inv.id}`}
                        title={inv.opened_at ? `Açıldı: ${new Date(inv.opened_at).toLocaleString()}` : "Açıldı"}
                        className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700 ring-1 ring-sky-200"
                      >
                        <Eye className="h-3 w-3" /> Açıldı
                      </span>
                    )}
                    {(inv.visited_at || inv.accepted) && (
                      <span
                        data-testid={`invitation-clicked-${inv.id}`}
                        title={inv.visited_at ? `Linke tıklandı: ${new Date(inv.visited_at).toLocaleString()}` : "Linke tıklandı"}
                        className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200"
                      >
                        <MousePointerClick className="h-3 w-3" /> Tıklandı
                      </span>
                    )}
                    {warm && (
                      <span
                        data-testid={`invitation-warm-${inv.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 ring-1 ring-amber-300"
                      >
                        🔥 İlgilendi, katılmadı
                      </span>
                    )}
                    {inv.reminder_sent_at && !inv.accepted && (
                      <span
                        data-testid={`invitation-reminded-${inv.id}`}
                        title={`Hatırlatma gönderildi: ${new Date(inv.reminder_sent_at).toLocaleString()}`}
                        className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 ring-1 ring-violet-200"
                      >
                        <BellRing className="h-3 w-3" /> Hatırlatıldı
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-zinc-400" />
                    <span className="truncate font-medium text-zinc-900">
                      {inv.email}
                    </span>
                    {inv.name && (
                      <span className="text-xs text-zinc-500">
                        — {inv.name}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[11px] text-zinc-500">
                    → {inv.tenant_name}
                    {inv.trial_ends_at && (
                      <>
                        {" · "}
                        <span className="font-medium">
                          {t("platform.invitations.trial_ends", {
                            date: new Date(inv.trial_ends_at).toLocaleDateString(),
                          })}
                        </span>
                      </>
                    )}
                  </div>
                  {inv.notes && (
                    <div className="mt-2 rounded-md bg-zinc-50 px-3 py-1.5 text-xs italic text-zinc-600">
                      &ldquo;{inv.notes}&rdquo;
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {warm && (
                    <Button
                      size="sm"
                      onClick={() => followUp(inv)}
                      disabled={followingUpId === inv.id}
                      data-testid={`platform-invitation-followup-${inv.id}`}
                      className="h-8 gap-1 bg-amber-500 text-xs text-white hover:bg-amber-600"
                      title={
                        inv.follow_up_sent_at
                          ? `Son takip: ${new Date(inv.follow_up_sent_at).toLocaleString()} (${inv.follow_up_count || 1}. kez)`
                          : "Kişisel takip e-postası gönder"
                      }
                    >
                      {followingUpId === inv.id ? (
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="h-3.5 w-3.5" />
                      )}
                      Takip et
                      {inv.follow_up_count > 0 && (
                        <span className="ml-0.5 rounded-full bg-white/25 px-1.5 text-[10px] font-bold">
                          {inv.follow_up_count}
                        </span>
                      )}
                    </Button>
                  )}
                  {inv.status === "pending" && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToken(inv.id, inv.token)}
                        data-testid={`platform-invitation-copy-${inv.id}`}
                        className="h-8 text-xs"
                        title={t("platform.invitations.copy_token")}
                      >
                        {copiedId === inv.id ? (
                          <>
                            <Check className="mr-1 h-3.5 w-3.5 text-emerald-600" />
                            {t("platform.invitations.copied")}
                          </>
                        ) : (
                          <>
                            <Copy className="mr-1 h-3.5 w-3.5" />
                            {t("platform.invitations.copy_token")}
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resend(inv.id)}
                        data-testid={`platform-invitation-resend-${inv.id}`}
                        className="h-8 text-xs"
                      >
                        <RefreshCw className="mr-1 h-3.5 w-3.5" />
                        {t("platform.invitations.resend")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmRevoke(inv.id)}
                        data-testid={`platform-invitation-revoke-${inv.id}`}
                        className="h-8 text-xs text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <TenantInviteDialog
        open={showInvite}
        onOpenChange={setShowInvite}
        onCreated={() => load()}
      />

      <BulkInviteDialog
        open={showBulkInvite}
        onOpenChange={setShowBulkInvite}
        onSent={() => load()}
      />

      <AlertDialog
        open={!!confirmRevoke}
        onOpenChange={(o) => !o && setConfirmRevoke(null)}
      >
        <AlertDialogContent data-testid="platform-invitation-revoke-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("platform.invitations.revoke_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("platform.invitations.revoke_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revoke(confirmRevoke)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="platform-invitation-revoke-confirm-btn"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
